/*
 * Copyright (C) 2026 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

// Pure, dependency-free strftime formatter. Extracted from the legacy
// @canvas/i18n implementation (ui/shared/i18n/i18nObj.js I18n.strftime), with
// the day/month names passed in explicitly rather than looked up from the
// old I18n locale-data global.
export interface DateNames {
  abbr_day_names: string[]
  abbr_month_names: (string | null)[] // 1-based: index 0 is null
  day_names: string[]
  month_names: (string | null)[] // 1-based: index 0 is null
  meridian: [string, string]
}

const padding = (n: number | string, pad = '00', len = 2) => {
  const s = pad + n.toString()
  return s.substr(s.length - len)
}

export function strftime(date: Date, format: string, names: DateNames): string {
  const {abbr_day_names, abbr_month_names, day_names, meridian, month_names} = names

  const weekDay = date.getDay()
  const day = date.getDate()
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const dayOfYear =
    1 +
    Math.round(
      (new Date(year, month - 1, day).getTime() - new Date(year, 0, 1).getTime()) / 86400000,
    )
  const hour = date.getHours()
  let hour12 = hour
  const meridianIndex = hour > 11 ? 1 : 0
  const secs = date.getSeconds()
  const mils = date.getMilliseconds()
  const mins = date.getMinutes()
  const offset = date.getTimezoneOffset()
  const epochOffset = Math.floor(date.getTime() / 1000)
  const absOffsetHours = Math.floor(Math.abs(offset / 60))
  const absOffsetMinutes = Math.abs(offset) - absOffsetHours * 60
  const timezoneoffset = `${offset > 0 ? '-' : '+'}${
    absOffsetHours.toString().length < 2 ? `0${absOffsetHours}` : absOffsetHours
  }${absOffsetMinutes.toString().length < 2 ? `0${absOffsetMinutes}` : absOffsetMinutes}`

  if (hour12 > 12) {
    hour12 -= 12
  } else if (hour12 === 0) {
    hour12 = 12
  }

  /*
    not implemented:
      %N  // nanoseconds
      %6N // microseconds
      %9N // nanoseconds
      %U  // week number of year, starting with the first Sunday as the first day of the 01st week (00..53)
      %V  // week number of year according to ISO 8601 (01..53) (week starts on Monday, week 01 is the one with the first Thursday of the year)
      %W  // week number of year, starting with the first Monday as the first day of the 01st week (00..53)
      %Z  // time zone name
  */
  const f = format
    .replace(
      /%([DFrRTv])/g,
      (_str, p1) =>
        ({
          D: '%m/%d/%y',
          F: '%Y-%m-%d',
          r: '%I:%M:%S %p',
          R: '%H:%M',
          T: '%H:%M:%S',
          v: '%e-%b-%Y',
        })[p1 as 'D' | 'F' | 'r' | 'R' | 'T' | 'v'],
    )
    .replace(/%(%|-?[a-zA-Z]|3N)/g, (str, p1) => {
      switch (p1) {
        case 'a':
          return abbr_day_names[weekDay]
        case 'A':
          return day_names[weekDay]
        case 'b':
          return abbr_month_names[month] ?? ''
        case 'B':
          return month_names[month] ?? ''
        case 'd':
          return padding(day)
        case '-d':
          return String(day)
        case 'e':
          return padding(day, ' ')
        case 'h':
          return abbr_month_names[month] ?? ''
        case 'H':
          return padding(hour)
        case '-H':
          return String(hour)
        case 'I':
          return padding(hour12)
        case '-I':
          return String(hour12)
        case 'j':
          return padding(dayOfYear, '00', 3)
        case 'k':
          return padding(hour, ' ')
        case '-k':
          return String(hour)
        case 'l':
          return padding(hour12, ' ')
        case '-l':
          return String(hour12)
        case 'L':
          return padding(mils, '00', 3)
        case 'm':
          return padding(month)
        case '-m':
          return String(month)
        case 'M':
          return padding(mins)
        case '-M':
          return String(mins)
        case 'n':
          return '\n'
        case '3N':
          return padding(mils, '00', 3)
        case 'p':
          return meridian[meridianIndex]
        case 'P':
          return meridian[meridianIndex].toLowerCase()
        case 's':
          return String(epochOffset)
        case 'S':
          return padding(secs)
        case '-S':
          return String(secs)
        case 't':
          return '\t'
        case 'u':
          return String(weekDay || weekDay + 7)
        case 'w':
          return String(weekDay)
        case 'y':
          return padding(year)
        case '-y':
          return padding(year).replace(/^0+/, '')
        case 'Y':
          return String(year)
        case 'z':
          return timezoneoffset
        case '%':
          return '%'
        default:
          return str
      }
    })

  return f
}
